// How the badges move without costing the phone much. A badge's motion is paid for every frame,
// wherever it is, so:
//
// - They move only where someone can see them: on screen, and not under an open sheet (a sheet's
//   own badges keep moving). Each badge is watched (an IntersectionObserver, and coverPage() from
//   the sheets); one out of sight holds still.
// - What has to redraw the drawing (the Phoenix's SMIL, gold's shine, Ablaze's flowing fire) runs
//   on one clock here, 30 times a second, not the 60 the browser would draw it at. Each step
//   repaints the badges it moves; the motion is slow and small, and at 30 it looks the same. Their
//   SVG timelines stay paused and are moved by setCurrentTime(); the fire's two shared gradients
//   (badges.ts) are slid by hand.
// - What doesn't (embers, Ablaze's glow) is HTML that the GPU moves on its own (badges.ts's
//   emberLayer); the .still class pauses it out of sight.
//
// Nothing here runs with reduced motion: the drawings hold still, and the CSS drops its motion.

type Watched = {
  box: HTMLElement
  /** The drawing, when its own timeline moves it (the Phoenix, gold). */
  svg: SVGSVGElement | null
  /** Burns with the shared fire. */
  blaze: boolean
  onScreen: boolean
  running: boolean
  /** Seconds of its timeline shown so far. */
  t: number
}

const watched = new Map<HTMLElement, Watched>()
let covers = 0
let observer: IntersectionObserver | null = null
let fire: readonly SVGGradientElement[] = []

const STEP_MS = 1000 / 30
// The fire flows up 192 (its reflected gradients repeat) every 3 s.
const FLOW_S = 3
let flowed = 0
let timer: ReturnType<typeof setTimeout> | undefined
let last = 0

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

function apply(w: Watched) {
  const running = w.onScreen && (covers === 0 || !!w.box.closest('.sheet-layer, .ach-reveal'))
  if (running === w.running) return
  w.running = running
  w.box.classList.toggle('still', !running)
}

function tick() {
  const now = performance.now()
  const dt = last ? Math.min(now - last, 250) / 1000 : 0
  last = now
  let burning = false
  for (const w of watched.values()) {
    if (!w.running) continue
    if (w.blaze) burning = true
    if (w.svg) {
      w.t += dt
      w.svg.setCurrentTime(w.t)
    }
  }
  if (burning) {
    flowed = (flowed + dt) % FLOW_S
    const y = Math.round((-192 * flowed * 100) / FLOW_S) / 100
    for (const g of fire) g.setAttribute('gradientTransform', `translate(0 ${y})`)
  }
  timer = undefined
  schedule()
}

// Keep the clock going while anything in sight moves; stop it when nothing does.
function schedule() {
  const moving = !reducedMotion() && [...watched.values()].some((w) => w.running && (w.svg || (w.blaze && fire.length)))
  if (!moving) {
    clearTimeout(timer)
    timer = undefined
    last = 0
  } else if (timer === undefined) {
    timer = setTimeout(tick, last ? STEP_MS : 0)
  }
}

const update = () => {
  for (const w of watched.values()) apply(w)
  schedule()
}

/** Ablaze's two gradients in the shared defs, slid while a burning badge is in sight. */
export function setFire(gradients: readonly SVGGradientElement[]) {
  fire = gradients
  if (typeof matchMedia === 'function') matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', update)
  schedule()
}

/**
 * Watch a moving badge's box: `blaze` when it burns with the shared fire. A drawing with its own
 * motion (SMIL) inside is paused here and moved by the clock. Call the returned function when it goes.
 */
export function watchBadge(box: HTMLElement, blaze: boolean): () => void {
  // Without an observer (old browsers), everything just runs as drawn.
  if (typeof IntersectionObserver === 'undefined') return () => {}
  const drawing = box.querySelector<SVGSVGElement>('svg')
  const svg = drawing?.querySelector('animate, animateTransform') ? drawing : null
  svg?.pauseAnimations()
  observer ??= new IntersectionObserver((entries) => {
    for (const e of entries) {
      const w = watched.get(e.target as HTMLElement)
      if (w) { w.onScreen = e.isIntersecting; apply(w) }
    }
    schedule()
  }, { rootMargin: '40px' })
  // On screen until the observer says otherwise, so nothing starts frozen.
  const w: Watched = { box, svg, blaze, onScreen: true, running: false, t: 0 }
  watched.set(box, w)
  observer.observe(box)
  apply(w)
  schedule()
  return () => {
    observer?.unobserve(box)
    watched.delete(box)
    schedule()
  }
}

/** A sheet or the reveal is open over the page; call the returned function when it closes. */
export function coverPage(): () => void {
  covers++
  update()
  return () => {
    covers--
    update()
  }
}
